import { MARKETING_REPORT_ALLOWED_HOSTS } from '../../../constants';

const DEFAULT_REPORT_HOSTS: readonly string[] = [];

type AllowedHostEntry =
  | {
      kind: 'exact';
      value: string;
    }
  | {
      kind: 'suffix';
      value: string;
    };

export interface AllowedHostsConfig {
  exactHosts: ReadonlySet<string>;
  suffixHosts: readonly string[];
}

export function createAllowedHostsConfig(
  rawHosts: readonly string[],
): AllowedHostsConfig {
  const exactHosts = new Set<string>();
  const suffixHosts = new Set<string>();

  rawHosts.forEach((entry) => {
    const normalized = normalizeHostEntry(entry);
    if (!normalized) return;
    if (normalized.kind === 'exact') {
      exactHosts.add(normalized.value);
      return;
    }
    suffixHosts.add(normalized.value);
  });

  return {
    exactHosts,
    suffixHosts: Array.from(suffixHosts),
  };
}

export function isSecureMarketingReportUrl(
  url: string | null | undefined,
  config: AllowedHostsConfig,
): url is string {
  if (!url) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') {
    return false;
  }

  if (parsed.username || parsed.password) {
    return false;
  }

  const host = parsed.host.toLowerCase();

  if (config.exactHosts.has(host)) {
    return true;
  }

  return config.suffixHosts.some((suffix) => {
    const normalizedSuffix = suffix.toLowerCase();
    return host === normalizedSuffix || host.endsWith(`.${normalizedSuffix}`);
  });
}

export function isSafeReportDownloadUrl(
  value: string | null | undefined,
): value is string {
  const configuredHosts =
    MARKETING_REPORT_ALLOWED_HOSTS.length > 0
      ? MARKETING_REPORT_ALLOWED_HOSTS
      : DEFAULT_REPORT_HOSTS;
  const applicationHost =
    typeof window !== 'undefined' ? [window.location.host] : [];
  const config = createAllowedHostsConfig([
    ...configuredHosts,
    ...applicationHost,
  ]);

  return isSecureMarketingReportUrl(value, config);
}

function normalizeHostEntry(
  entry: string | null | undefined,
): AllowedHostEntry | null {
  if (!entry) return null;

  const trimmed = entry.trim();
  if (!trimmed) {
    return null;
  }

  const lower = trimmed.toLowerCase();

  if (lower.startsWith('*.')) {
    const suffix = lower.slice(2).replace(/^\.+/, '');
    if (!suffix) return null;
    return {
      kind: 'suffix',
      value: suffix,
    };
  }

  const host = extractHost(lower);
  if (!host) {
    return null;
  }

  return {
    kind: 'exact',
    value: host,
  };
}

function extractHost(value: string): string | null {
  const candidate = value.includes('://') ? value : `https://${value}`;
  try {
    return new URL(candidate).host.toLowerCase();
  } catch {
    return null;
  }
}
